import type { ReactNode } from 'react';
import { Badge, Card, SectionLabel } from '@wundo/ui';
import type { Skill } from '@/lib/skills.ts';

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      <div className='mt-1 text-sm text-ink'>{children}</div>
    </div>
  );
}

/**
 * What the skill is, before any numbers about it: the description the agent
 * matches on, and the four dials that decide when it gets picked.
 */
export function SkillDefinitionCard({ skill }: { skill: Skill }) {
  return (
    <Card as='section' aria-label='Definition' className='flex flex-col gap-4'>
      <div className='flex items-start justify-between gap-4'>
        <div className='min-w-0'>
          <h2 className='font-mono text-sm font-semibold text-ink'>{skill.name}</h2>
          <p className='mt-1 text-sm text-ink-secondary'>{skill.description}</p>
        </div>
        <Badge tone={skill.visibility === 'public' ? 'success' : 'neutral'}>
          {skill.visibility}
        </Badge>
      </div>

      <div className='grid grid-cols-2 gap-4 border-t border-line pt-4 mobile:grid-cols-4'>
        <Fact label='Boost'>
          <span className='font-mono'>{skill.boost.toFixed(2)}</span>
        </Fact>
        <Fact label='Freshness'>{skill.freshness}d</Fact>
        <Fact label='Flags'>
          <span className='flex flex-wrap gap-1'>
            {skill.triggerable && <Badge tone='info'>triggerable</Badge>}
            {skill.recommendable && <Badge tone='info'>recommendable</Badge>}
            {skill.notes_enabled && <Badge tone='neutral'>notes</Badge>}
          </span>
        </Fact>
        <Fact label='Tools'>
          <span className='text-ink-secondary'>
            {skill.tool_calls.length} tools · {skill.context_providers.length} providers
          </span>
        </Fact>
      </div>
    </Card>
  );
}
